# Hirer relationships

The SEEK API authorizes operations based on a set of relationships between you and your SEEK hirers.
Each [use case] requires a corresponding relationship to allow you to act on behalf of the hirer:

- An [`ApplicationExport`] relationship allows you to export the hirer’s [candidate profiles] & [attachments].

- An [`ApplicationPrefill`] relationship allows you to retrieve SEEK Profile information to pre-fill an external apply form.

- A [`JobPosting`] relationship allows you to price, post & update job ads on behalf of the hirer.

- A [`ProactiveSourcing`] relationship allows you to [upload candidates] & [unposted positions] belonging to the hirer.

- A [`CandidateProfiles`] relationship allows you to display the SEEK Profile Panel and query for SEEK candidate profiles.

- A [`CandidateVerification`] relationship allows you to request verification of candidates with SEEK Pass and query candidate identity verification statuses.

Functionality shared between use cases will accept multiple relationship types.
For example, [position openings] can be managed with either a `JobPosting` or `ProactiveSourcing` relationship.

You can view a list of your SEEK hirers and their relationships on the [Developer Dashboard hirers page].

## Hirer onboarding

SEEK must explicitly configure hirer relationships as part of onboarding a SEEK hirer to your software.
Direct hirers to the SEEK “Connect your system” page:

```text label="URL"
https://employer.seek.com/partners/link-your-software/
```

Embed this URL verbatim in your software and support collateral.
Once opened,
it will redirect hirers to the appropriate [regional] subdomain based on IP address.

[regional]: /introduction#multi-market-support

GraphQL operations will fail with a [`FORBIDDEN` error] if a required relationship hasn’t been configured.
You should contact the hirer in cases where this is unexpected.

SEEK hirers in specific market segments commonly have multiple SEEK accounts aligned to divisions within their organizations.
It’s important that your platform supports hirers with multiple SEEK accounts per Section B, Clause 73b of the [SEEK API Terms of Use].

SEEK will provide the hirer with their hirer ID for configuration in your software.
At the same time, the SEEK API will emit a [`HirerRelationshipChanged` event](#events).
While you can source the hirer ID from this event,
SEEK requires that you manually triage each hirer setup to link the SEEK hirer with the relevant account in your software.

If you capture the hirer ID in a self-service text field:

1. The field must accept the hirer ID in full.

   Per the earlier [SEEK API conventions] section,
   object identifiers must not be disassembled into their component parts.

2. The value must be trimmed before it is passed to the SEEK API.

   The SEEK API will not recognise hirer IDs with leading or trailing whitespace.

3. The value may be validated upfront through the SEEK API.

   You can make a [`hiringOrganization` query](#hiringorganization) to provide faster feedback to hirers when they enter an invalid ID.

4. The association must be appropriately triaged and authorized by your team.

   A hirer must not be able to enter another hirer’s ID into your software and gain access to their data.

import { HirerIdInput } from './hirer-relationships';

<HirerIdInput />

## Before you begin

Hirer relationship queries are typically made from your backend with a [partner token](/auth/partner-tokens).

While these queries work with a [browser token](/auth/browser-tokens) that has a `query:organizations` scope,
they will only return the hirer that the browser token is scoped to.

## Operations

### `hiringOrganization`

The [`hiringOrganization` query] will list your configured relationships with a SEEK hirer.
If no relationship has been configured the query will fail with a [`FORBIDDEN` error].

The [Developer Dashboard hirers page] wraps this query in a friendly user interface.

```graphql
query ($id: String!) {
  hiringOrganization(id: $id) {
    name
    seekApiCapabilities {
      relationshipTypeCodes
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json
{
  "hiringOrganization": {
    "name": "Acme Corp",
    "seekApiCapabilities": {
      "relationshipTypeCodes": [
        "ApplicationExport",
        "ApplicationPrefill",
        "CandidateProfiles",
        "CandidateVerification",
        "JobPosting",
        "ProactiveSourcing"
      ]
    }
  }
}
```

### `hiringOrganizations`

The [`hiringOrganizations` query] will return a paginated list of all SEEK hirers you have a given relationship with.

The [Developer Dashboard hirers page] wraps this query in a friendly user interface.

```graphql
query ($schemeId: String!, $relationshipTypeCodes: [String!]!, $first: Int) {
  hiringOrganizations(
    schemeId: $schemeId
    filter: { relationshipTypeCodes: $relationshipTypeCodes }
    first: $first
  ) {
    edges {
      node {
        id {
          value
        }
        name
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "schemeId": "seekAnzPublicTest",
  "relationshipTypeCodes": ["ApplicationExport"],
  "first": 5
}
```

```json
{
  "hiringOrganizations": {
    "edges": [
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
          },
          "name": "Acme Corp"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:A3HL8E4w"
          },
          "name": "Contoso"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:AYAWCibZ"
          },
          "name": "Fabrikam"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:FWzEydsm"
          },
          "name": "PixelArcade Studios"
        }
      },
      {
        "node": {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:C2p2SCBR"
          },
          "name": "Prescott Pharmaceuticals"
        }
      }
    ],
    "pageInfo": {
      "hasNextPage": true,
      "endCursor": "seekAnzPublicTest:hiringOrganizationCursor:seek:C2p2SCBR"
    }
  }
}
```

### `self`

When using a [browser token], the [`self` query] will return the hirer the browser token is scoped to.
This can be used to list a hirer’s relationships directly from your frontend without having to explicitly specify the hirer’s ID.

```graphql label="Query" hirerId="seekAnzPublicTest:organization:seek:9YQA3jYK" scope="query:organizations"
query {
  self {
    hirer {
      id {
        value
      }
      name
      seekApiCapabilities {
        relationshipTypeCodes
      }
    }
  }
}
```

```jsonc label="Result"
{
  "self": {
    // Based on the request's browser token
    "hirer": {
      "id": {
        "value": "seekAnzPublicTest:organization:seek:9YQA3jYK"
      },
      "name": "Vandelay Industries",
      "seekApiCapabilities": {
        "relationshipTypeCodes": ["ApplicationExport", "ApplicationPrefill"]
      }
    }
  }
}
```

## Events

`HirerRelationshipChanged` events are emitted whenever a relationship between you and a hirer is added or removed.
The hirer relationships in your software can be kept in sync with SEEK using these events.

This is an example of a [webhook](/events/webhooks) body containing a `HirerRelationshipChanged` event:

```json sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:RJrWs6Kw13TvACTTXG6qZg",
      "type": "HirerRelationshipChanged",

      "createDateTime": "2020-10-20T23:13:58.804Z",

      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:RNzsabxEX56cuRepCD9A8j",
  "url": "https://example.com/webhook",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

Once you receive an event, you can use its [`hirerId`] to query the current state of your relationship with that hirer from the [`hiringOrganization` query](#hiringorganization).

If all relationships have been removed with the hirer, a [`FORBIDDEN` error] will be returned.

[`ApplicationExport`]: /use-cases/application-export
[`ApplicationPrefill`]: /use-cases/apply-with-seek
[`FORBIDDEN` error]: /graphql/error-responses#forbidden
[`hirerId`]: /schema/webhooks/HirerRelationshipChangedEvent#/property/hirerId
[`hiringOrganization` query]: /schema/query/hiringOrganization
[`hiringOrganizations` query]: /schema/query/hiringOrganizations
[`JobPosting`]: /use-cases/job-posting
[`ProactiveSourcing`]: /use-cases/proactive-sourcing
[`CandidateProfiles`]: /use-cases/seek-profile
[`CandidateVerification`]: /use-cases/seek-profile/verify-with-seek-pass
[`self` query]: /schema/query/self
[attachments]: /use-cases/application-export/attachments
[browser token]: /auth/browser-tokens
[candidate profiles]: /use-cases/application-export/candidate-profiles
[Developer Dashboard hirers page]: /manage/hirers
[position openings]: /use-cases/job-posting/position-openings
[SEEK API conventions]: /graphql/seek-api-conventions#object-identifiers
[SEEK API Terms of Use]: https://au.employer.seek.com/partners/terms-of-use
[unposted positions]: /use-cases/proactive-sourcing/unposted-positions
[upload candidates]: /use-cases/proactive-sourcing/sharing-a-candidate
[use case]: /
